import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Calendar,
  DollarSign,
  Users,
  MapPin,
  CheckCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { BookingWithOptionsResponse, BookingOptionResponse } from "@/types/activities";

interface ConfirmationData {
  vendorBookingReference: string;
  vendorBookedAt?: string;
  finalPrice?: number;
  finalLocation?: string;
  finalCapacity?: number;
  confirmationNotes?: string;
}

interface FinalBookingConfirmationProps {
  booking: BookingWithOptionsResponse;
  selectedOption: BookingOptionResponse;
  open: boolean;
  onClose: () => void;
  onConfirm: (data: ConfirmationData) => Promise<void>;
  className?: string;
}

export default function FinalBookingConfirmation({
  booking,
  selectedOption,
  open,
  onClose,
  onConfirm,
  className
}: FinalBookingConfirmationProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<ConfirmationData>({
    defaultValues: {
      vendorBookingReference: '',
      vendorBookedAt: '',
      finalPrice: 0,
      finalLocation: '',
      finalCapacity: 0,
      confirmationNotes: ''
    }
  });

  const formData = watch();

  // Helper function to convert ISO date string to datetime-local format (YYYY-MM-DDTHH:MM)
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Format: YYYY-MM-DDTHH:MM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Update form values when selectedOption changes
  useEffect(() => {
    if (open && selectedOption) {
      // Default vendorBookedAt to current date/time (not in the future)
      const now = new Date();
      setValue('vendorBookingReference', '');
      setValue('vendorBookedAt', formatDateForInput(now.toISOString()));
      setValue('finalPrice', selectedOption.proposedPrice || selectedOption.marketplacePrice || 0);
      setValue('finalLocation', selectedOption.proposedLocation || '');
      setValue('finalCapacity', selectedOption.proposedCapacity || booking.estimatedParticipantsCount);
      setValue('confirmationNotes', '');
    }
  }, [open, selectedOption, booking, setValue]);

  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFinalConfirmation = async () => {
    if (!formData.vendorBookingReference) {
      toast({
        title: "Validation Error",
        description: "Vendor booking reference is required",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirm(formData);

      toast({
        title: "Booking confirmed!",
        description: "The booking has been successfully confirmed with the vendor."
      });

      onClose();
      reset();
    } catch (error: any) {
      toast({
        title: "Confirmation failed",
        description: error.message || "Failed to confirm booking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Final Booking Confirmation
          </DialogTitle>
          <DialogDescription>
            Complete the final confirmation for {booking.activityName} with {selectedOption.vendorName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFinalConfirmation)}>
          <div className="space-y-6">
            {/* Booking Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-muted-foreground">Event:</span>
                    <p>{booking.activityName}</p>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Organization:</span>
                    <p>{booking.organizationName}</p>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Vendor:</span>
                    <p>{selectedOption.vendorName}</p>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Service:</span>
                    <p>{selectedOption.serviceOptionName}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Confirmation Form */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Confirmation Details</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="vendorBookingReference">Vendor Booking Reference *</Label>
                  <Input
                    id="vendorBookingReference"
                    {...register("vendorBookingReference", { required: true, minLength: 1 })}
                    placeholder="Enter vendor's booking reference number (required)"
                  />
                  {errors.vendorBookingReference && (
                    <p className="text-sm text-destructive">Vendor booking reference is required</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendorBookedAt">Booking Date</Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="vendorBookedAt"
                      type="datetime-local"
                      {...register("vendorBookedAt")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="finalPrice">Final Price (AED)</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="finalPrice"
                      type="number"
                      step="0.01"
                      {...register("finalPrice", { min: 0 })}
                    />
                  </div>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="finalLocation">Final Location</Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="finalLocation"
                      {...register("finalLocation")}
                      placeholder="Enter the confirmed venue address"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="finalCapacity">Final Capacity</Label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="finalCapacity"
                      type="number"
                      {...register("finalCapacity", { min: 1 })}
                    />
                  </div>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="confirmationNotes">Confirmation Notes</Label>
                  <Textarea
                    id="confirmationNotes"
                    {...register("confirmationNotes")}
                    placeholder="Internal notes for this booking confirmation..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                "Confirming..."
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Confirm Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}